import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type {
  ExportProgress,
  ExportProgressContextType,
  ExportProgressProviderProps,
} from './types';

const defaultExportProgress: ExportProgress = {
  total: 0,
  value: 0,
  hasFailed: false,
  isExporting: false,
  loadMore: () => {},
  initialEntitiesCount: 0,
  gridService: undefined,
};

export const ExportProgressContext = createContext<
  ExportProgressContextType | undefined
>(undefined);

export const useExportProgressContext = () => {
  const context = useContext(ExportProgressContext);
  if (!context) {
    return undefined;
  }
  return context;
};

export const ExportProgressProvider: React.FC<
  ExportProgressProviderProps
> = ({ children }) => {
  const [exportProgress, setExportProgress] =
    useState<ExportProgress>(defaultExportProgress);

  const handleExport = useCallback(() => {
    setExportProgress((prev) => {
      if (prev.gridService) {
        prev.gridService.exportAllDataAsCsv(
          prev.loadMore,
          prev.initialEntitiesCount,
          ({
            total,
            value,
            hasFailed,
            isExporting,
          }: Partial<ExportProgress>) => {
            setExportProgress((curr) => ({
              ...curr,
              value: value !== undefined ? value : curr.value,
              total: total !== undefined ? total : curr.total,
              hasFailed:
                hasFailed !== undefined ? hasFailed : curr.hasFailed,
              isExporting:
                isExporting !== undefined
                  ? isExporting
                  : curr.isExporting,
            }));
          },
        );
      }
      return prev;
    });
  }, []);

  // Memoize value to avoid unnecessary rerenders
  const value = useMemo(
    () => ({
      exportProgress,
      setExportProgress,
      handleExport,
    }),
    [exportProgress, handleExport],
  );

  return (
    <ExportProgressContext.Provider value={value}>
      {children}
    </ExportProgressContext.Provider>
  );
};
